import { useMeetingStore, useWorkplaceStore } from '@host/stores';
import { isPostHogEnabled } from '@host/config/posthog';
import { trackEvent } from '@host/utils/posthog-track';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';
import { jitsiService } from '@/shared/singletons/jitsi-singleton';

type MeetingContext = {
  room_type: string | null;
  is_breakout: boolean;
  is_auditorium: boolean;
  auditorium_role: string | null;
  is_guest: boolean;
};

// The Jitsi call the user is in now. jitsiParticipants includes the local user, so a peak of
// 1 means they sat alone in the room and 2+ means an actual meeting took place.
let session: { joinedAt: number; peakParticipants: number; context: MeetingContext } | null = null;
let screenShareStartedAt: number | null = null;
let whisper: { startedAt: number; role: 'initiator' | 'recipient' } | null = null;

const participantCount = () => Object.keys(useMeetingStore.getState().jitsiParticipants).length;

const endScreenShare = (closeReason: 'stopped' | 'page_unload') => {
  if (screenShareStartedAt === null) return;
  const duration = Math.round((Date.now() - screenShareStartedAt) / 1000);
  screenShareStartedAt = null;
  trackEvent(
    'screen_share_stopped',
    { duration_seconds: duration, close_reason: closeReason },
    closeReason === 'page_unload' ? { transport: 'sendBeacon' } : undefined
  );
};

const endWhisper = (closeReason: 'ended' | 'page_unload') => {
  if (!whisper) return;
  const { startedAt, role } = whisper;
  whisper = null;
  trackEvent(
    'whisper_ended',
    { role, duration_seconds: Math.round((Date.now() - startedAt) / 1000) },
    closeReason === 'page_unload' ? { transport: 'sendBeacon' } : undefined
  );
};

/** Called once a join attempt settles for the room the user still wants. */
export const trackMeetingJoinOutcome = (
  joined: boolean,
  options?: { isBreakoutRoom?: boolean; auditorium?: boolean }
) => {
  if (!isPostHogEnabled) return;
  const meeting = useMeetingStore.getState();
  const context: MeetingContext = {
    room_type: useWorkplaceStore.getState().currentRoom?.type ?? null,
    is_breakout: !!options?.isBreakoutRoom,
    is_auditorium: meeting.isAuditorium || !!options?.auditorium,
    auditorium_role: meeting.auditoriumRole,
    is_guest: isCurrentUserGuest(),
  };
  const joinMs = meeting.roomJoinStartedAt ? Date.now() - meeting.roomJoinStartedAt : null;

  if (!joined) {
    trackEvent('meeting_join_failed', { ...context, join_ms: joinMs });
    return;
  }
  // A rejoin without a leave in between (e.g. after a dropped connection) closes the old one.
  trackMeetingLeft({ trackingReason: 'rejoined' });
  const count = participantCount();
  session = { joinedAt: Date.now(), peakParticipants: count, context };
  trackEvent('meeting_joined', { ...context, participant_count: count, join_ms: joinMs });
};

/**
 * Called before leaveJitsiMeeting clears the participant list. `leaveReason` is the one passed
 * to Jitsi; `trackingReason` names leaves Jitsi gets no reason for (e.g. a normal room switch).
 */
export const trackMeetingLeft = ({
  leaveReason,
  trackingReason,
  pageUnload = false,
}: {
  leaveReason?: string;
  trackingReason?: string;
  pageUnload?: boolean;
} = {}) => {
  if (!session) return;
  const { joinedAt, peakParticipants, context } = session;
  session = null;
  if (!pageUnload) endScreenShare('stopped');
  const reason =
    leaveReason?.replace(/-/g, '_') ??
    trackingReason ??
    (useMeetingStore.getState().isMeetingHungUp ? 'hang_up' : 'other');
  trackEvent(
    'meeting_left',
    {
      ...context,
      duration_seconds: Math.round((Date.now() - joinedAt) / 1000),
      peak_participant_count: peakParticipants,
      leave_reason: pageUnload ? 'page_unload' : reason,
    },
    pageUnload ? { transport: 'sendBeacon' } : undefined
  );
};

/** `someone_entered`: the call restarts by itself when another person walks into the room. */
export type CallTrigger = 'user' | 'break' | 'someone_entered';

export const trackCallHangUp = (isHungUp: boolean, trigger: CallTrigger) => {
  trackEvent(isHungUp ? 'call_ended' : 'call_restarted', {
    trigger,
    is_guest: isCurrentUserGuest(),
  });
};

export const trackReactionSent = (emojiType: string) => {
  trackEvent('meeting_reaction_sent', { reaction: emojiType, is_hand_raise: emojiType === 'hand' });
};

export const trackVirtualBackgroundChanged = (backgroundType: string) => {
  trackEvent('virtual_background_changed', { background_type: backgroundType });
};

export const trackWhisperRequested = () => trackEvent('whisper_requested');

export const trackWhisperRejected = () => trackEvent('whisper_rejected', { reason: 'busy' });

/**
 * Watches the meeting store for state that changes from many places: the participant peak,
 * the local screen share (stopped from our button, the browser's own bar, or by leaving), and
 * the whisper session (both sides report it, told apart by `role`).
 */
export const registerMeetingTracking = () => {
  if (!isPostHogEnabled) return;

  useMeetingStore.subscribe((state, prev) => {
    if (state.jitsiParticipants !== prev.jitsiParticipants) {
      const participants = Object.values(state.jitsiParticipants);
      if (session) {
        session.peakParticipants = Math.max(session.peakParticipants, participants.length);
      }
      const sharing = participants.some((p) => p.isLocal && p.screenTrack);
      if (sharing && screenShareStartedAt === null) {
        screenShareStartedAt = Date.now();
        trackEvent('screen_share_started', { participant_count: participants.length });
      } else if (!sharing && Object.keys(state.jitsiParticipants).length > 0) {
        // An emptied list is a leave, which trackMeetingLeft already closed the share for.
        endScreenShare('stopped');
      }
    }

    if (state.whisperSessionStatus !== prev.whisperSessionStatus) {
      if (state.whisperSessionStatus === 'connected' && !whisper) {
        // whisperMeetingInfo.from is the requester on both sides of the handshake.
        const role =
          state.whisperMeetingInfo?.from === jitsiService?.getMyUserId()
            ? 'initiator'
            : 'recipient';
        whisper = { startedAt: Date.now(), role };
        trackEvent('whisper_started', { role });
      } else if (state.whisperSessionStatus !== 'connected') {
        endWhisper('ended');
      }
    }
  });

  window.addEventListener('pagehide', () => {
    endScreenShare('page_unload');
    endWhisper('page_unload');
    trackMeetingLeft({ pageUnload: true });
  });
};
